/**
 * Sign-in help, ported from the le-portal-ui-v3 prototype (src/LoginHelpDialog.tsx).
 *
 * Reached from the "?" on the sign-in card. Content is static guidance — browsers,
 * browser settings, and the regional support aliases — so the dialog holds no state
 * beyond its open flag.
 */
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  makeStyles,
  mergeClasses,
  shorthands,
  Text,
  Title3,
  tokens,
} from '@fluentui/react-components';
import {
  DesktopRegular,
  DismissRegular,
  GlobeRegular,
  MailRegular,
  PersonAccountsRegular,
  PhoneRegular,
  QuestionCircleRegular,
  SettingsRegular,
} from '@fluentui/react-icons';
import { LOGIN_NARROW_MEDIA } from '../loginStyles';

// Fluent switches to whole-dialog scrolling below 360px; this dialog keeps its title fixed.
const SHORT_VIEWPORT_MEDIA = '@media screen and (max-height: 359px)';

const bodyText = {
  color: tokens.colorNeutralForeground1,
  fontSize: '15px',
  lineHeight: 1.6,
};

const tableCell = {
  ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
  textAlign: 'left' as const,
  ...shorthands.borderBottom(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke2),
};

// The dialog renders in a portal outside the gate, but these read Fluent's theme tokens,
// which the portal inherits from FluentProvider, so nothing has to be copied across.
const useStyles = makeStyles({
  surface: {
    display: 'flex',
    flexDirection: 'column',
    width: 'min(900px, calc(100vw - 32px))',
    maxWidth: '900px',
    maxHeight: 'min(820px, calc(100dvh - 32px))',
    ...shorthands.padding(0),
    ...shorthands.overflow('hidden'),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius('12px'),
    boxShadow: tokens.shadow4,
    [SHORT_VIEWPORT_MEDIA]: {
      ...shorthands.padding(0),
      ...shorthands.borderWidth(tokens.strokeWidthThin),
      overflowY: 'hidden',
    },
  },

  body: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    maxHeight: 'none',
    ...shorthands.gap(0),
  },

  titleBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
    ...shorthands.gap(tokens.spacingHorizontalM),
    ...shorthands.padding(tokens.spacingVerticalXL, tokens.spacingHorizontalXXL),
    ...shorthands.borderBottom(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke2),
    [LOGIN_NARROW_MEDIA]: {
      ...shorthands.padding(tokens.spacingVerticalL),
    },
  },

  title: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalM),
    fontSize: '22px',
    lineHeight: 1.25,
  },

  titleIcon: {
    display: 'grid',
    placeItems: 'center',
    width: '40px',
    height: '40px',
    flexShrink: 0,
    color: tokens.colorNeutralForegroundOnBrand,
    backgroundColor: tokens.colorBrandBackground,
    ...shorthands.borderRadius(tokens.borderRadiusCircular),
    '& svg': {
      width: '22px',
      height: '22px',
    },
  },

  content: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    ...shorthands.gap(tokens.spacingVerticalXXL, tokens.spacingHorizontalXXL),
    minHeight: 0,
    flexShrink: 1,
    ...shorthands.margin(0),
    ...shorthands.padding(tokens.spacingVerticalXL, tokens.spacingHorizontalXXL, tokens.spacingVerticalXXL),
    overflowY: 'auto',
    backgroundColor: tokens.colorNeutralBackground1,
    [SHORT_VIEWPORT_MEDIA]: {
      overflowY: 'auto',
    },
    [LOGIN_NARROW_MEDIA]: {
      gridTemplateColumns: '1fr',
      ...shorthands.padding(tokens.spacingVerticalL),
    },
  },

  section: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalM),
    ...shorthands.padding(tokens.spacingVerticalXL),
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.border(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius('10px'),
    '& p': {
      ...shorthands.margin(0),
      ...bodyText,
    },
    '& code': {
      ...shorthands.padding('1px', tokens.spacingHorizontalXS),
      color: tokens.colorNeutralForeground1,
      backgroundColor: tokens.colorNeutralBackground4,
      ...shorthands.borderRadius(tokens.borderRadiusMedium),
    },
    [LOGIN_NARROW_MEDIA]: {
      ...shorthands.padding(tokens.spacingVerticalL),
    },
  },

  contact: {
    backgroundColor: tokens.colorBrandBackground2,
    ...shorthands.borderColor(tokens.colorNeutralStroke1),
  },

  sectionWide: {
    gridColumn: '1 / -1',
    [LOGIN_NARROW_MEDIA]: {
      gridColumn: 'auto',
    },
  },

  sectionLead: {
    borderLeftWidth: '3px',
    borderLeftColor: tokens.colorBrandStroke1,
  },

  sectionHeading: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalM),
    '& h2': {
      ...shorthands.margin(0),
      fontSize: tokens.fontSizeBase400,
    },
  },

  icon: {
    display: 'grid',
    placeItems: 'center',
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: '32px',
    width: '32px',
    height: '32px',
    color: tokens.colorBrandForeground1,
    backgroundColor: tokens.colorBrandBackground2,
    ...shorthands.borderRadius(tokens.borderRadiusXLarge),
    '& svg': {
      width: '18px',
      height: '18px',
    },
  },

  settingsList: {
    ...shorthands.margin(0),
    paddingLeft: tokens.spacingHorizontalXL,
    ...bodyText,
  },

  urlList: {
    display: 'grid',
    ...shorthands.gap(tokens.spacingVerticalXS),
    ...shorthands.margin(0),
    paddingTop: tokens.spacingVerticalS,
    paddingLeft: 0,
    listStyleType: 'none',
    overflowWrap: 'anywhere',
    '& li': {
      ...shorthands.padding(0),
      color: tokens.colorNeutralForeground1,
      fontFamily: tokens.fontFamilyMonospace,
      fontSize: tokens.fontSizeBase200,
    },
  },

  tableWrap: {
    overflowX: 'auto',
  },

  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '15px',
    '& th': {
      ...tableCell,
      color: tokens.colorNeutralForeground3,
      fontWeight: tokens.fontWeightSemibold,
    },
    '& td': tableCell,
    '& tbody tr:last-child td': {
      borderBottomWidth: 0,
    },
  },

  contactList: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    ...shorthands.gap(tokens.spacingVerticalS, tokens.spacingHorizontalXL),
    ...shorthands.padding(0),
    ...shorthands.margin(0),
    listStyleType: 'none',
    '& li': {
      display: 'flex',
      alignItems: 'center',
      ...shorthands.gap(tokens.spacingHorizontalS),
      minWidth: 0,
      ...shorthands.padding(tokens.spacingVerticalXS, 0),
      color: tokens.colorNeutralForeground1,
      fontSize: '15px',
    },
    '& li > svg': {
      flexGrow: 0,
      flexShrink: 0,
      flexBasis: 'auto',
      color: tokens.colorBrandForeground1,
    },
    '& a': {
      color: tokens.colorBrandForegroundLink,
      overflowWrap: 'anywhere',
    },
    [LOGIN_NARROW_MEDIA]: {
      gridTemplateColumns: '1fr',
    },
  },

  contactPhone: {
    gridColumn: '1 / -1',
  },
});

interface LoginHelpDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const supportContacts = [
  ['United States', 'usleainfo@microsoft.com'],
  ['EU Member States', 'euleainfo@microsoft.com'],
  ['Brazil', 'lelatam@microsoft.com'],
  ['Other Countries', 'intleainfo@microsoft.com'],
] as const;

export function LoginHelpDialog({ open, onOpenChange }: LoginHelpDialogProps) {
  const styles = useStyles();

  return (
    <Dialog open={open} onOpenChange={(_, data) => onOpenChange(data.open)}>
      <DialogSurface className={styles.surface}>
        <DialogBody className={styles.body}>
          <div className={styles.titleBar}>
            <DialogTitle className={styles.title}>
              <span className={styles.titleIcon} aria-hidden="true">
                <QuestionCircleRegular />
              </span>
              Sign-in help
            </DialogTitle>
            <Button
              appearance="subtle"
              icon={<DismissRegular />}
              aria-label="Close login help"
              onClick={() => onOpenChange(false)}
            />
          </div>
          <DialogContent className={styles.content}>
            <section className={mergeClasses(styles.section, styles.sectionWide, styles.sectionLead)}>
              <div className={styles.sectionHeading}>
                <span className={styles.icon} aria-hidden="true">
                  <PersonAccountsRegular />
                </span>
                <Title3 as="h2">Setting up your account</Title3>
              </div>
              <p>
                To access the Law Enforcement Portal, sign in with your Microsoft account.
                Only accounts of the kind this portal is enabled for can sign in; any other
                account is turned away on the Microsoft sign-in page.
              </p>
            </section>

            <section className={styles.section}>
              <div className={styles.sectionHeading}>
                <span className={styles.icon} aria-hidden="true">
                  <DesktopRegular />
                </span>
                <Title3 as="h2">Supported browsers</Title3>
              </div>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th scope="col">Browser</th>
                      <th scope="col">Supported versions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Chrome</td>
                      <td>Latest</td>
                    </tr>
                    <tr>
                      <td>Firefox</td>
                      <td>Latest</td>
                    </tr>
                    <tr>
                      <td>Edge</td>
                      <td>2 most recent major versions</td>
                    </tr>
                    <tr>
                      <td>Safari</td>
                      <td>2 most recent major versions</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section className={styles.section}>
              <div className={styles.sectionHeading}>
                <span className={styles.icon} aria-hidden="true">
                  <SettingsRegular />
                </span>
                <Title3 as="h2">Browser settings</Title3>
              </div>
              <p>
                If you have a supported browser and are still experiencing issues, you may
                need to adjust your browser settings or have your IT department adjust them
                for you.
              </p>
              <Text weight="semibold">Required settings:</Text>
              <ul className={styles.settingsList}>
                <li>
                  Allow cookies from <code>*.microsoftonline.com</code> and <code>*.live.com</code>
                </li>
                <li>
                  Add these URLs to an allowlist that permits network traffic:
                  <ul className={styles.urlList}>
                    <li>https://v3.leportal.microsoft.com</li>
                    <li>https://login.live.com</li>
                    <li>https://login.microsoftonline.com</li>
                  </ul>
                </li>
              </ul>
            </section>

            <section className={mergeClasses(styles.section, styles.contact, styles.sectionWide)}>
              <div className={styles.sectionHeading}>
                <span className={styles.icon} aria-hidden="true">
                  <MailRegular />
                </span>
                <Title3 as="h2">Still having problems?</Title3>
              </div>
              <p>
                If you are still having login issues, please email the appropriate support
                alias for assistance.
              </p>
              <ul className={styles.contactList}>
                {supportContacts.map(([region, email]) => (
                  <li key={region}>
                    <GlobeRegular aria-hidden="true" />
                    <span>
                      {region}: <a href={`mailto:${email}`}>{email}</a>
                    </span>
                  </li>
                ))}
                <li className={styles.contactPhone}>
                  <PhoneRegular aria-hidden="true" />
                  <span>
                    Phone: <a href="tel:+14257221299">+1 425-722-1299</a>, option 2
                  </span>
                </li>
              </ul>
            </section>
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
